import { Container, useTheme } from '@mui/material';
import { useState, useEffect } from 'react';

import { ChatWindowDragDropWrapper } from './ChatWindowDragDropWrapper';
import { ChatWindowFooter } from './ChatWindowFooter';
import { ChatWindowHeader } from './ChatWindowHeader';
import { ChatWindowInput } from './ChatWindowInput';
import { ChatWindowMessages } from './ChatWindowMessages';
import { ChatWindowProgress } from './ChatWindowProgress';
import { ChatWindowInputFileError, ChatWindowProps } from './ChatWindowTypes';

const ChatWindowComponent = ({
  width = 428,
  height = '100%',
  borderRadius = '0',
  border = 'none',
  title,
  headerRightContent,
  messages,
  isWaitingForResponse,
  isReady,
  progressState,
  userPrompt,
  onUserPromptChange,
  onSendMessage,
  isOwner,
  userInitials,
  suggestedActions,
  disclaimerText,
  progressMessageMap,
  progressPlacement = 'fixed',
  chatWindowRef,
  messagesEndRef,
  inputPlaceholder,
  fileUploadConfig,
  fileUploadDragDropTexts,
  fileError,
  setFileError,
}: ChatWindowProps & {
  fileError: ChatWindowInputFileError;
  setFileError: (error: ChatWindowInputFileError) => void;
}) => {
  const theme = useTheme();
  const isFixedProgress = progressPlacement === 'fixed';

  // Scroll messages to bottom when progress changes height (expand/collapse/step change)
  // Only applies in fixed mode — inline mode handles scrolling via useScrollMessageIntoView
  useEffect(() => {
    if (isFixedProgress) {
      messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
    }
  }, [progressState, messagesEndRef, isFixedProgress]);

  return (
    <Container
      sx={{
        display: 'flex',
        flex: 2,
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        minWidth: `${width}px`,
        width: `${width}px`,
        height,
        borderRadius,
        border,
        '&.MuiContainer-root': {
          '@media (min-width: 600px)': {
            padding: '0',
          },
          padding: '0',
        },
      }}
    >
      {/* Header */}
      <ChatWindowHeader title={title} rightContent={headerRightContent} />

      {/* Message Content */}
      <ChatWindowMessages
        messages={messages}
        userInitials={userInitials}
        chatWindowRef={chatWindowRef}
        messagesEndRef={messagesEndRef}
        scrollPaddingBottom={progressPlacement === 'inline' ? '50vh' : undefined}
      />

      {/* Progress — fixed between messages and input (only in fixed placement mode) */}
      {isFixedProgress && progressState?.currentStep && (
        <ChatWindowProgress
          message={
            progressState.currentStep.message !== undefined &&
            progressState.currentStep.message !== null &&
            progressState.currentStep.message !== ''
              ? progressState.currentStep.message
              : progressMessageMap?.[progressState.currentStep.step] ||
                progressState.currentStep.step
          }
          avProgress={progressState.avProgress}
        />
      )}
      {isFixedProgress && isWaitingForResponse && !progressState?.currentStep && (
        <ChatWindowProgress message="Understanding request" />
      )}

      {/* Input and Footer Section */}
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: `0 ${theme.spacing(6)} !important`,
        }}
      >
        <ChatWindowInput
          userPrompt={userPrompt}
          onUserPromptChange={onUserPromptChange}
          onSendMessage={onSendMessage}
          isOwner={isOwner}
          isWaitingForResponse={isWaitingForResponse}
          isReady={isReady}
          inputPlaceholder={inputPlaceholder}
          suggestedActions={suggestedActions}
          fileUploadConfig={fileUploadConfig}
          fileUploadDragDropTexts={fileUploadDragDropTexts}
          fileError={fileError}
          setFileError={setFileError}
        />

        {/* Footer */}
        <ChatWindowFooter disclaimerText={disclaimerText} />
      </Container>
    </Container>
  );
};

export const ChatWindow = (chatWindowProps: ChatWindowProps) => {
  const [fileError, setFileError] = useState<ChatWindowInputFileError>(null);

  return chatWindowProps.fileUploadConfig ? (
    <ChatWindowDragDropWrapper
      maxFileSizeMB={chatWindowProps.fileUploadConfig.maxFileSizeMB}
      accept={chatWindowProps.fileUploadConfig.accept}
      onFileSelected={chatWindowProps.fileUploadConfig.onFileSelected}
      isReady={chatWindowProps.isReady}
      dragDropTexts={chatWindowProps.fileUploadDragDropTexts}
      fileError={fileError}
      setFileError={setFileError}
    >
      <ChatWindowComponent {...chatWindowProps} fileError={fileError} setFileError={setFileError} />
    </ChatWindowDragDropWrapper>
  ) : (
    <ChatWindowComponent {...chatWindowProps} fileError={fileError} setFileError={setFileError} />
  );
};
